import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { authService } from '@shared/services/auth.service';
import { useAuthStore } from '@shared/store/auth.store';
import { usePermissions } from '@shared/hooks/usePermissions';
import { hasOwnerAccount } from '@shared/lib/accounts';
import type { LoginCredentials } from '@shared/types/auth';
import { logger } from '@shared/utils/logger';
import { storage } from '@shared/utils/storage';
import { queryClient } from '@shared/lib/query-client';
import { broadcastSignedOut } from '@shared/auth/session-coordination';
import { useCompanyStore } from '@shared/store/company.store';

/**
 * Primary auth hook consumed by UI. Reads reactive state from the store and
 * exposes login/logout mutations plus the centralized permission helpers, so a
 * screen never has to reach into the store or reason about roles directly.
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const user = useAuthStore((state) => state.user);
  const isInitializing = useAuthStore((state) => state.isInitializing);
  const setSession = useAuthStore((state) => state.setSession);
  const setUser = useAuthStore((state) => state.setUser);
  const clear = useAuthStore((state) => state.clear);
  const accounts = useAuthStore((state) => state.accounts);
  const activeAccountId = useAuthStore((state) => state.activeAccountId);
  const rememberAccount = useAuthStore((state) => state.rememberAccount);
  const forgetAccount = useAuthStore((state) => state.forgetAccount);
  const { can, canAny, canAll, scopeFor, isOwner, permissions } = usePermissions();

  const loginMutation = useMutation({
    // If the login payload carries no authorization block, it is read from
    // /auth/me before the session is applied — otherwise the app would start
    // with no permissions at all.
    mutationFn: async (credentials: LoginCredentials) => {
      const session = await authService.login(credentials);
      const user = await authService.resolveAuthorizedUser(session);
      return { ...session, user };
    },
    onSuccess: (session) => {
      // The session carries the effective authorization payload; the store
      // derives permissions/scopes from it in one place.
      setSession(session.user, session, session);
      // Remember (or refresh) this account. Keyed on user.id, so signing in
      // again as someone already saved updates that entry instead of adding a
      // duplicate — and makes them active.
      rememberAccount(session.user, session);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => {
      const sessionId = useAuthStore.getState().activeSessionId;
      broadcastSignedOut(sessionId);
      // At once, not after the network answers: the user is leaving now.
      storage.purgeSessionScoped();
      return authService.logout(sessionId);
    },
    // Clear local state regardless of network outcome — the user intends to
    // leave, and the refresh cookie is invalidated server-side. `clear()` also
    // drops the authorization slice.
    // Sign out this account only: drop its saved entry, keep the others.
    onSettled: () => {
      const { activeAccountId: id } = useAuthStore.getState();
      if (id) forgetAccount(id);
      clear();
      useCompanyStore.getState().clear();
      queryClient.clear();
      storage.purgeSessionScoped();
    },
    onError: (error) => logger.warn('Logout request failed; clearing session anyway', error),
  });

  const login = useCallback(
    (credentials: LoginCredentials) => loginMutation.mutateAsync(credentials),
    [loginMutation],
  );

  const logout = useCallback(() => logoutMutation.mutateAsync(), [logoutMutation]);

  /**
   * Re-read `/auth/me` and re-apply the authorization payload. Call it after an
   * action that can change the current user's own access (e.g. editing the
   * permissions of a role they hold) so the UI reflects the new rights without
   * a reload.
   */
  const refreshAuthorization = useCallback(async () => {
    const nextUser = await authService.getCurrentUser();
    setUser(nextUser);
    return nextUser;
  }, [setUser]);

  /**
   * Make a saved account active: rotate its session, swap the in-memory token,
   * user and authorization, and drop the previous account's cached data.
   * Throws when that session can no longer be refreshed, so the caller can send
   * the user through the normal login form.
   */
  const switchAccount = useCallback(
    async (userId: string) => {
      const target = useAuthStore.getState().accounts.find((a) => a.userId === userId);
      if (!target) throw new Error('That account is no longer saved.');
      const session = await authService.refresh(target.sessionId);
      const nextUser = await authService.resolveAuthorizedUser(session);
      // Cached data belongs to the account that fetched it.
      queryClient.clear();
      setSession(nextUser, session, session);
      rememberAccount(nextUser, session);
      return nextUser;
    },
    [queryClient, setSession, rememberAccount],
  );

  /** Sign a specific account out. Returns the account to fall back to, if any. */
  const removeAccount = useCallback(
    async (userId: string) => {
      const target = useAuthStore.getState().accounts.find((a) => a.userId === userId);
      const isActive = useAuthStore.getState().activeAccountId === userId;
      try {
        if (target?.sessionId || isActive) await authService.logout(target?.sessionId ?? null);
      } catch (error) {
        logger.warn('Logout request failed; forgetting the account anyway', error);
      }
      const successor = forgetAccount(userId);
      // That account's session has ended, active or not.
      storage.purgeSessionScoped();
      if (isActive) {
        clear();
        queryClient.clear();
      }
      return successor;
    },
    [clear, forgetAccount, queryClient],
  );

  return {
    user,
    isAuthenticated: Boolean(user),
    isInitializing,
    login,
    logout,
    refreshAuthorization,
    // Multi-account
    accounts,
    activeAccountId,
    switchAccount,
    removeAccount,
    /** Add Account is offered only while an owner account is saved. */
    canAddAccount: hasOwnerAccount(accounts),
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    // Centralized authorization surface.
    isOwner,
    permissions,
    can,
    canAny,
    canAll,
    scopeFor,
  } as const;
}

export function useLogin() {
  const { login, isLoggingIn, loginError } = useAuth();
  return { login, isPending: isLoggingIn, error: loginError } as const;
}
export function useLogout() {
  const { logout } = useAuth();
  return { logout } as const;
}
export function useRefresh() {
  return useMutation({
    mutationFn: async () => {
      const session = await authService.refresh();
      const user = await authService.resolveAuthorizedUser(session);
      useAuthStore.getState().setSession(user, session, session);
      return session;
    },
  });
}
export function useCurrentUser() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.getCurrentUser,
    enabled: Boolean(useAuthStore((s) => s.accessToken)),
  });
}

// Used by imperative logout paths as well as React components.
export async function logoutAllDevices(): Promise<void> {
  const sessionId = useAuthStore.getState().activeSessionId;
  try {
    await authService.logoutAll();
  } finally {
    // Only tabs on THIS session follow; another saved account stays signed in.
    broadcastSignedOut(sessionId);
    const store = useAuthStore.getState();
    if (store.activeAccountId) store.forgetAccount(store.activeAccountId);
    store.clear();
    useCompanyStore.getState().clear();
    queryClient.clear();
  }
}

/**
 * Local sign-out after the backend already revoked the session (a password
 * change revokes every session of the account, the current one included).
 */
export function clearRevokedSession(): void {
  const store = useAuthStore.getState();
  broadcastSignedOut(store.activeSessionId);
  if (store.activeAccountId) store.forgetAccount(store.activeAccountId);
  store.clear();
  useCompanyStore.getState().clear();
  queryClient.clear();
}
